import React, { useState, useTransition } from 'react';
import EventIcon from '@mui/icons-material/CalendarToday';
import { CircularProgress, InputAdornment } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ENTER_DATE } from 'common/constants/process-management';
import theme from 'theme';
import { dbShortDate } from 'utilities';
import { ProcessManagementQuestionsFormDateInputProps } from './types';

const ProcessManagementQuestionsFormDateInput = ({
  answerToDisplay,
  handleAnswerQuestionAction,
}: ProcessManagementQuestionsFormDateInputProps) => {
  const [draftDate, setDraftDate] = useState<Date | null>(
    answerToDisplay ? new Date(answerToDisplay) : null
  );

  const [isPending, startTransition] = useTransition();

  const handleCommit = (date: Date | null) => {
    if (!date || Number.isNaN(date.getTime())) {
      return;
    }

    const formattedDate = dbShortDate(date) ?? '';
    /* React 19:
     * We wrap `handleAnswerQuestion` in React 19's `startTransition` to mark the 
     * resulting state updates (and the subsequent unmounting of this component) 
     * as low-priority. 
     * This allows React to prioritize the DatePicker's internal closing animations, 
     * ensuring the UI stays fluid and responsive. By deferring the unmount, we 
     * prevent the component from vanishing mid-animation while `isPending` provides 
     * clear visual feedback of the background progress.
     */
    startTransition(() => {
      handleAnswerQuestionAction(formattedDate);
    });
  };

  return (
    <DatePicker
      value={draftDate}
      onChange={newValue => setDraftDate(newValue)}
      // Prevents the picker from closing when a date is clicked.
      closeOnSelect={false}
      // Only commit when the "OK" button in the Action Bar is clicked.
      onAccept={handleCommit}
      format="MM/dd/yyyy"
      disabled={isPending}
      slotProps={{
        textField: {
          fullWidth: true,
          variant: 'outlined',
          placeholder: ENTER_DATE,
          sx: {
            '& .MuiOutlinedInput-root': {
              minHeight: '56px',
              borderRadius: '8px',
              border: `1px solid ${theme.palette.primary[700]}`,
              color: theme.palette.primary[700],
              fontWeight: 400,
              fontSize: '1rem',
              '& fieldset': { border: 'none' },
              '&:hover': {
                borderColor: theme.palette.primary[800],
              },
              '&.Mui-focused': {
                borderColor: theme.palette.primary[900],
              },
            },
          },
          InputProps: {
            startAdornment: (
              <InputAdornment position="start">
                {isPending ? (
                  <CircularProgress size={20} sx={{ color: theme.palette.primary[700] }} />
                ) : (
                  <EventIcon sx={{ color: theme.palette.primary[700] }} />
                )}
              </InputAdornment>
            ),
          },
        },
        actionBar: {
          // Actions: 'accept' maps to the "OK" button, 'cancel' to "Cancel"
          actions: ['cancel', 'accept'],
        },
      }}
    />
  );
};

export default ProcessManagementQuestionsFormDateInput;
